import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { api, inr } from "@/lib/api";
import { servicesFallback } from "@/data/marketing";

export default function Services() {
  const [services, setServices] = useState(servicesFallback);

  useEffect(() => {
    api.get("/services")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) {
          setServices(data.map((service) => ({
            ...servicesFallback.find((fallback) => fallback.slug === service.slug),
            ...service,
          })));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="marketing-page" data-testid="services-page">
      <header className="page-container pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow">What we do</p>
            <h1 className="font-display font-semibold text-5xl sm:text-7xl lg:text-[92px] leading-[0.92] tracking-[-0.06em]">
              The right disciplines, around one outcome.
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-white/60 leading-relaxed">Choose a focused engagement or bring us the whole problem. We shape the team around the result, not a fixed list of deliverables.</p>
            <Link to="/booking" className="text-link mt-6" data-testid="services-intro-book">Talk through your project <ArrowUpRight size={15} /></Link>
          </div>
        </div>
      </header>

      <section className="bg-[#F2EFE7] text-[#11130F]" aria-labelledby="service-list-title">
        <div className="page-container py-16 sm:py-24">
          <h2 id="service-list-title" className="sr-only">Services</h2>
          <div className="border-t border-black/20">
            {services.map((service, index) => {
              const Icon = Icons[service.icon] || Icons.Sparkles;
              const deliverables = service.deliverables || servicesFallback[index]?.deliverables || [];
              return (
                <article key={service.service_id || service.slug} className="grid lg:grid-cols-12 gap-5 lg:gap-8 py-9 sm:py-12 border-b border-black/20 group" data-testid={`service-card-${service.slug}`}>
                  <div className="lg:col-span-1 flex items-start gap-4">
                    <span className="font-mono text-xs text-black/45">{String(index + 1).padStart(2, "0")}</span>
                    <Icon size={24} className="lg:hidden" aria-hidden="true" />
                  </div>
                  <div className="hidden lg:block lg:col-span-1"><Icon size={26} aria-hidden="true" /></div>
                  <div className="lg:col-span-4">
                    <h3 className="font-display font-semibold text-2xl sm:text-3xl">{service.title}</h3>
                    <p className="mt-3 text-black/60 leading-relaxed max-w-lg">{service.desc}</p>
                  </div>
                  <ul className="lg:col-span-3 space-y-2 text-sm text-black/65">
                    {deliverables.map((item) => <li key={item} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0" aria-hidden="true" /> {item}</li>)}
                  </ul>
                  <div className="lg:col-span-3 lg:text-right flex lg:block items-center justify-between gap-4">
                    <div><span className="block text-[10px] uppercase tracking-[.14em] text-black/45">Typical starting point</span><strong className="font-display text-xl mt-1 block">{inr(service.starts_at)}</strong></div>
                    <Link to="/booking" className="inline-flex items-center gap-2 mt-4 font-semibold text-sm border-b border-black/30 pb-1 hover:border-black" data-testid={`service-cta-${service.slug}`}>Discuss scope <ArrowRight size={15} /></Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="page-container section-space" aria-labelledby="engagement-title">
        <div className="section-heading-grid">
          <div>
            <p className="eyebrow">Ways to work together</p>
            <h2 id="engagement-title" className="section-title">Enough structure to move quickly. Enough flexibility to stay useful.</h2>
          </div>
          <p className="section-intro">We confirm scope, owners, milestones and commercial terms before work begins. You get a weekly progress review and access to the working files throughout.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {[
            ["Focused sprint", "1–3 weeks", "A clear answer to one high-value problem: an audit, prototype, conversion pass or technical proof."],
            ["Product engagement", "6–14 weeks", "Strategy, design and engineering for a launch, redesign or significant new product capability."],
            ["Ongoing partner", "Monthly", "A senior cross-functional team for planned releases, optimisation and product care."],
          ].map(([title, timing, copy], index) => (
            <article key={title} className="tracing-beam rounded-xl p-7 sm:p-8" data-testid={`engagement-${index + 1}`}>
              <span className="label-mono">{timing}</span>
              <h3 className="font-display font-semibold text-2xl mt-8">{title}</h3>
              <p className="text-white/55 leading-relaxed mt-3">{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="page-container pb-20 sm:pb-28">
        <div className="commercial-cta">
          <div><p className="eyebrow">Not sure where to start?</p><h2>Bring the business problem. We’ll help frame the right engagement.</h2></div>
          <div className="cta-side"><p>We’ll use the first conversation to clarify the opportunity, identify risks and suggest the smallest useful next step.</p><Link to="/booking" className="pill-btn ink" data-testid="services-final-cta">Book a discovery call <ArrowUpRight size={16} /></Link></div>
        </div>
      </section>
    </div>
  );
}
